/** React binding for `DebouncedSaveController` (see that file's own doc
 * comment for the full rationale — spec §15 debounced autosave +
 * VistoeraTASK-00300's `scheduleAndWaitForSettled`). Kept as a thin
 * wrapper specifically so the actual state machine can be unit-tested
 * headlessly in plain Node without a React render harness; this file
 * only owns the React-lifetime concerns (constructing the controller
 * once per component instance, keeping its `save` callback current
 * across re-renders, mirroring its status into React state, and
 * disposing its timer on unmount).
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import { DebouncedSaveController, type SaveStatus } from './debouncedSaveController'

export type { SaveStatus }

export function useDebouncedSave<T>(save: (payload: T) => Promise<void>, delayMs = 800) {
  const [status, setStatus] = useState<SaveStatus>('idle')
  const controllerRef = useRef<DebouncedSaveController<T> | null>(null)
  if (!controllerRef.current) {
    controllerRef.current = new DebouncedSaveController<T>(save, delayMs, setStatus)
  }
  controllerRef.current.setSave(save)

  const cancelPending = useCallback(() => {
    controllerRef.current?.cancelPending()
  }, [])

  const flushNow = useCallback((): Promise<boolean> => {
    return controllerRef.current?.flushNow() ?? Promise.resolve(true)
  }, [])

  const schedule = useCallback((payload: T) => {
    controllerRef.current?.schedule(payload)
  }, [])

  const scheduleAndWaitForSettled = useCallback((payload: T): Promise<boolean> => {
    return controllerRef.current?.scheduleAndWaitForSettled(payload) ?? Promise.resolve(true)
  }, [])

  useEffect(
    () => () => {
      controllerRef.current?.dispose()
    },
    [],
  )

  return { status, schedule, flushNow, cancelPending, scheduleAndWaitForSettled }
}
